/**
 * Workspace details and renaming.
 *
 * The MVP has exactly one workspace per customer and no invitation flow, so this screen shows the
 * current workspace and its owner, and allows the name to be edited. There is deliberately no
 * member-management surface: team invitations and advanced roles are post-MVP.
 *
 * Plan limits are not repeated here. The Billing page lists every plan's limits with the current one
 * marked, and the Overview shows the monthly credits; a third copy on this page was one more place
 * for the same numbers to be read in a less readable form.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '../../lib/api-client';
import { useUpdateWorkspace, useWorkspace } from '../../lib/api';
import { Button, Card, Field, FormError, TextInput } from '../../components/ui';

const schema = z.object({ name: z.string().trim().min(1, 'Required.').max(100, 'Too long.') });
type WorkspaceForm = z.infer<typeof schema>;

export function WorkspaceRoute() {
  const { data: workspace, isLoading } = useWorkspace();
  const updateWorkspace = useUpdateWorkspace();
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<WorkspaceForm>({
    resolver: zodResolver(schema),
    values: { name: workspace?.name ?? '' },
  });

  useEffect(() => {
    if (workspace !== undefined) reset({ name: workspace.name });
  }, [workspace, reset]);

  const onSubmit = handleSubmit((values) => {
    setFormError(null);
    setSaved(false);
    updateWorkspace.mutate(values, {
      onSuccess: () => setSaved(true),
      onError: (error) => {
        setFormError(error instanceof ApiError ? error.message : 'Could not save the workspace.');
      },
    });
  });

  if (isLoading || workspace === undefined) {
    return <p className="text-sm text-neutral-500">Loading...</p>;
  }

  return (
    <>
      <div>
        <h1 className="text-xl font-semibold">Workspace</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Your workspace owns your API keys, usage, and billing.
        </p>
      </div>

      <Card>
        <h2 className="text-base font-semibold">Details</h2>
        <form
          onSubmit={(event) => void onSubmit(event)}
          className="mt-4 flex flex-col gap-4"
          noValidate
        >
          <FormError message={formError} />
          <Field label="Name" htmlFor="workspace-name" error={errors.name?.message}>
            <TextInput
              id="workspace-name"
              invalid={errors.name !== undefined}
              {...register('name')}
            />
          </Field>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wide text-neutral-500">Identifier</dt>
              <dd className="font-mono text-xs">{workspace.id}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-neutral-500">Slug</dt>
              <dd className="font-mono text-xs">{workspace.slug}</dd>
            </div>
          </dl>
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={updateWorkspace.isPending}>
              {updateWorkspace.isPending ? 'Saving...' : 'Save changes'}
            </Button>
            {saved && (
              <span role="status" className="text-sm text-green-700">
                Saved
              </span>
            )}
          </div>
        </form>
      </Card>

      <Card>
        <h2 className="text-base font-semibold">Membership</h2>
        <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wide text-neutral-500">Owner</dt>
            <dd>{workspace.owner.email}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-neutral-500">Your role</dt>
            <dd>{workspace.membership.role}</dd>
          </div>
        </dl>
        <p className="mt-4 text-sm text-neutral-500">
          Inviting teammates and assigning roles are not part of this release.
        </p>
      </Card>
    </>
  );
}
